<template>
    <div class="enquiry-form">
        <TransitionModal>
            <Fixed
                v-if="enquiryType && !hideForm"
                @close="closeEnquiryForm"
            >
                <ModalWithHeader
                    :label="formTitle || 'Enquiry form'"
                    @close="closeEnquiryForm"
                >
                    <EnquiryFormForm
                        :disabled="fetching"
                        :errors="errors"
                        :is-logged="isLogged"
                        @submit="submit"
                    >
                        <template #title>
                            {{ formTitle }}
                        </template>

                        <template #submit>
                            {{ formSubmitLabel }}
                        </template>
                    </EnquiryFormForm>
                </ModalWithHeader>
            </Fixed>
        </TransitionModal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useEnquiryFormStore } from '@/stores/enquiryForm'
import { useEnquiryFormTranslations } from './useEnquiryFormTranslations'

import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { getUri } from '@/helpers/getCurrentUri'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { useHandleEnquiryResponse } from '@/composables/enquiry/useHandleEnquiryResponse'
import { useRegionPlugin } from '@/plugins/region'

import ModalWithHeader from '@/components/layout/modal/ModalWithHeader.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import EnquiryFormForm from './EnquiryFormForm.vue'
import TransitionModal from '../layout/modal/TransitionModal.vue'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useCreateEnquiryNewCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryNewCustomer.generated.ts'
import { useCreateEnquiryExistingCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryExistingCustomer.generated.ts'

const enquiryFormStore = useEnquiryFormStore()
const { closeEnquiryForm } = enquiryFormStore
const { enquiryType, enquiryImage, isFetching } = storeToRefs(enquiryFormStore)

const translations = useEnquiryFormTranslations()

const formTitle = computed(
    () => enquiryType.value && translations[enquiryType.value]?.title()
)

const formSubmitLabel = computed(
    () => enquiryType.value && translations[enquiryType.value]?.submit()
)

const commissionJunction = getCommissionJunctionInput()

const uri = getUri()
const currency = useCurrencyInput()
const { regionCode } = useRegionPlugin()
const { isLogged } = useAuth()

const newCustomerResponse = useCreateEnquiryNewCustomerMutation()
const { executeMutation: newCustomerMutation, fetching: newCustomerFetching } =
    newCustomerResponse

const existingCustomerResponse = useCreateEnquiryExistingCustomerMutation()
const {
    executeMutation: existingCustomerMutation,
    fetching: existingCustomerFetching,
} = existingCustomerResponse

const fetching = computed(
    () => newCustomerFetching.value || existingCustomerFetching.value
)

watch(fetching, (n) => (isFetching.value = n))

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryExistingCustomer'
    ),
    createResponseAndPath(newCustomerResponse, 'createEnquiryNewCustomer'),
])

const { handleEnquiryResponse } = useHandleEnquiryResponse()

const enquiryInput = computed(() => {
    if (!enquiryType.value) {
        return null
    }

    return {
        baseInput: {
            uri,
            currency: currency.value,
            region: regionCode.value,
        },
        image: enquiryImage.value,
        uri,
        type: enquiryType.value,
        commissionJunction,
    }
})

const hideForm = ref(false)

watch(
    enquiryType,
    (type) => {
        const input = enquiryInput.value

        if (!input) {
            return
        }

        if (!type) {
            return
        }

        if (!isLogged.value) {
            return
        }

        hideForm.value = true

        existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                hideForm.value = false
                // Error state should be displayed at this point
                return
            }

            if (data && data.createEnquiryExistingCustomer) {
                handleEnquiryResponse({
                    data: {
                        customer: undefined,
                    },
                    error,
                })

                closeEnquiryForm()
                hideForm.value = false
            }
        })
    },
    { flush: 'post' }
)

const { customerRegistrationInput, googleSignInToken } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const isAdmin = useIsAdmin()

const doSubmit = () => {
    const input = enquiryInput.value

    if (!input) {
        return
    }

    if (isLogged.value) {
        return existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                return undefined
            }

            return data?.createEnquiryExistingCustomer
        })
    }

    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    return newCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then(({ data, error }) => {
        if (error) {
            googleSignInToken.value = null

            return undefined
        }

        return data?.createEnquiryNewCustomer
    })
}

const submit = () =>
    doSubmit()?.then((data) => {
        if (!data) {
            // Error state should be displayed at this point
            return
        }

        handleEnquiryResponse({
            data:
                data.__typename === 'EnquiryResponseExistingCustomer'
                    ? undefined
                    : data,
            error: undefined,
        })

        closeEnquiryForm()
    })
</script>
